import React from "react";

import ChevronDown from "@uui/ChevronDown";
import FilterIcon from "@uui/FilterLines";
import { isEmpty, reject, uniqBy } from "lodash/fp";

import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  Box,
  Button,
  FormControlLabel,
  Typography,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { FloatingNavButton } from "@app/analytics/components/reports/FloatingNav";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import { FilterCount } from "@app/analytics/MeetingReport/styles";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import { areSpeakersEqual } from "@app/analytics/MeetingReport/utils";
import { TranscriptSpeaker } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Checkbox from "@rui/Checkbox";
import Menu, { MenuHeader } from "@rui/Menu";
import { Row, Column } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

const Indicator = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.shades.primary[500],
  height: "6px",
  width: "6px",
  borderRadius: "50%",
}));

const StyledAccordion = styled((props: AccordionProps) => (
  <Accordion disableGutters {...props} />
))({
  minWidth: "300px",
  "&::before": {
    backgroundColor: "transparent",
  },
});

const StyledAccordionSummary = styled((props: AccordionSummaryProps) => (
  <AccordionSummary
    {...props}
    expandIcon={
      <ChevronDown sx={{ height: "18px", width: "18px", color: "#000" }} />
    }
  />
))({
  height: "56px",
  border: "none",
  "&.Mui-expanded": {
    ".MuiTypography-root": {
      fontWeight: 500,
    },
  },
});

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.shades.ghost[300],
  padding: "4px 24px 16px",
}));

const TranscriptFilters = () => {
  const { anchorElement, onClick, open, onClose } = useAnchorElement();
  const { allSpeakers, filteredSpeakers, setFilteredSpeakers } =
    useTranscriptStore((state) => ({
      allSpeakers: state.allSpeakers,
      filteredSpeakers: state.filteredSpeakers,
      setFilteredSpeakers: state.setFilteredSpeakers,
    }));

  if (isEmpty(allSpeakers)) return null;

  const handleToggleSpeaker =
    (speaker: TranscriptSpeaker) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        Events.filterTranscriptBySpeakers();
        const newFilteredSpeakers = uniqBy("name", [
          ...filteredSpeakers,
          speaker,
        ]);
        setFilteredSpeakers(newFilteredSpeakers);
      } else {
        setFilteredSpeakers(
          reject(areSpeakersEqual(speaker), filteredSpeakers)
        );
      }
    };

  const handleClearFilters = () => setFilteredSpeakers(allSpeakers);

  const filterCount = allSpeakers.length - filteredSpeakers.length;
  const hasFilters = filterCount > 0;

  return (
    <>
      <TooltipWrapper placement="bottom" title="Filter transcript">
        <FloatingNavButton
          Icon={() => (
            <>
              <FilterIcon />
              {hasFilters && <FilterCount>{filterCount}</FilterCount>}
            </>
          )}
          onClick={onClick}
          active={open}
          sx={{ width: hasFilters ? "fit-content" : undefined }}
        >
          Filter
        </FloatingNavButton>
      </TooltipWrapper>
      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorElement}
        disablePadding
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuHeader variant="apart">
          <Row spacing={1} alignItems="center">
            <FilterIcon />
            <span>Filters</span>
            {hasFilters && <FilterCount>{filterCount}</FilterCount>}
          </Row>
          {hasFilters && (
            <Button onClick={handleClearFilters} sx={{ p: "2px 4px" }}>
              Clear all
            </Button>
          )}
        </MenuHeader>
        <StyledAccordion defaultExpanded>
          <StyledAccordionSummary>
            <Row alignItems="center" spacing={1}>
              <Typography>Speakers</Typography>
              {hasFilters && <Indicator />}
            </Row>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <Column>
              {allSpeakers.map((speaker) => {
                const isChecked = Boolean(
                  filteredSpeakers.find((s) => s.name === speaker.name)
                );
                return (
                  <FormControlLabel
                    key={speaker.name}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={handleToggleSpeaker(speaker)}
                      />
                    }
                    label={speaker.name}
                  />
                );
              })}
            </Column>
          </StyledAccordionDetails>
        </StyledAccordion>
      </Menu>
    </>
  );
};

export default TranscriptFilters;
